import {Children, cloneElement, forwardRef} from 'react';
import {assignInlineVars} from '@vanilla-extract/dynamic';
import cx from 'classnames';

import * as css from './Carousel.css';

export interface CarouselProps extends React.ComponentPropsWithRef<'div'> {
  /**
   * The gap between slides within the carousel.
   */
  gap?: number;
  /**
   * The widths of the carousel at different breakpoints. Can be a number which corresponds
   * to a pixel value or a string which corresponds to a css value like '100%'
   */
  widths: (number | string)[];
  /**
   * The paddings of the carousel at different breakpoints. Can be a number which corresponds
   * to a pixel value or a string which corresponds to a css value like '100%' or 'calc(xx)'
   */
  paddings?: (number | string)[];
  /**
   * Whether or not to fade the slides in and out on the left and right edges of the carousel
   */
  fade?: boolean;
}

const constructWidthVar = (val: string | number) => {
  return typeof val === 'number' ? `${val}px` : val;
};

const getPadding = (padding?: number | string) => {
  if (!padding) return '0px';

  if (typeof padding === 'string') {
    return padding;
  }

  return `${padding}px`;
};

/**
 * Carousel is a component that allows you to scroll through a list of items. It is responsive and will adjust the number of items shown based on the width of the container. To use this component with extrenal controls, you must wrap it in a CarouselProvider.
 */
export const Carousel = forwardRef<HTMLDivElement, CarouselProps>(
  ({children, className, gap = 24, widths, fade = false, paddings, ...otherProps}, ref) => (
    <div
      className={cx(css.root({fade}), className)}
      style={assignInlineVars({
        [css.gapVar]: `${gap}px`,
        [css.largePaddingVar]: getPadding(paddings?.[2]),
        [css.largeWidthVar]: constructWidthVar(widths[2]),
        [css.mediumPaddingVar]: getPadding(paddings?.[1]),
        [css.mediumWidthVar]: constructWidthVar(widths[1]),
        [css.smallPaddingVar]: getPadding(paddings?.[0]),
        [css.smallWidthVar]: constructWidthVar(widths[0]),
      })}
      {...otherProps}>
      <div
        aria-roledescription="carousel"
        role="list"
        ref={ref}
        className={css.scrollContainer({fade})}>
        {Children.toArray(children).map((child, i) =>
          cloneElement(child as React.ReactElement, {
            ['aria-roledescription']: 'slide',
            key: i,
            role: 'listitem',
          }),
        )}
      </div>
    </div>
  ),
);
